import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import SchoolHeader from './components/SchoolReports/SchoolHeader';
import DoubleProgressBar from './components/SchoolReports/DoubleProgressBar';
import GradeWiseParticipationChart from './components/SchoolReports/GradeWiseParticipationChart';
import GradeWiseDetails from './components/SchoolReports/GradeWiseDetails';
import Summary from './components/SchoolReports/Summary';
import Footer from './components/Footer/Footer';
import { ReportProvider } from './SchoolReportContext.tsx';
import { DynamicToolTip } from './components/DynamicToolTip/DynamicToolTip';
import target from '@/assets/svg/Schoolreports/mingcute.svg';
import loaderGif from '@/assets/gifs/LoaderGif.gif';
import i from '@/assets/svg/emsat/i.svg';
import { useSchoolDetailsPublic } from '@/hooks/useSchool';
import { fetchSchoolReportBySchoolId } from '@/services/schoolReportService';
import type { SchoolReportData } from '@/types/schoolReport';
import './SchoolReport.css';

function splitParticipation(total?: string): [string, string] | null {
  if (!total) return null;
  const [attempted, registered] = total.split('/');
  if (attempted == null || registered == null) return null;
  return [attempted, registered];
}

function extractErrorMessage(err: unknown): string {
  const axiosErr = err as {
    response?: { data?: { detail?: string; message?: string; error?: string } };
    message?: string;
  };
  return (
    axiosErr.response?.data?.detail ??
    axiosErr.response?.data?.message ??
    axiosErr.response?.data?.error ??
    axiosErr.message ??
    'Failed to fetch school report'
  );
}

/** Header label from Gateway v2/school/search-school → data.schools[0].school_name
 *  (banner SVG already includes "School Name :" label — do not prefix again)
 */
function formatSchoolHeaderName(name?: string, districtName?: string): string | undefined {
  const trimmedName = name?.trim();
  if (!trimmedName) return undefined;
  const district = districtName?.trim();
  return district ? `${trimmedName}, ${district}` : trimmedName;
}

export default function SchoolReport(): ReactElement {
  const { schoolId } = useParams<Record<string, string | undefined>>();
  const [reportData, setReportData] = useState<SchoolReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const trimmedSchoolId = schoolId?.trim() ?? '';
  const { data: schoolDetails } = useSchoolDetailsPublic(trimmedSchoolId);

  const fetchReportData = useCallback(async (signal?: { cancelled: boolean }) => {
    try {
      setLoading(true);
      if (!trimmedSchoolId) {
        throw new Error('SCHOOL_ID is required. Load a school on the dashboard first.');
      }
      const data = await fetchSchoolReportBySchoolId(trimmedSchoolId);
      if (signal?.cancelled) return;
      setReportData(data);
      setError(null);
    } catch (err) {
      if (signal?.cancelled) return;
      setError(extractErrorMessage(err));
      setReportData(null);
    } finally {
      if (!signal?.cancelled) setLoading(false);
    }
  }, [trimmedSchoolId]);

  useEffect(() => {
    const signal = { cancelled: false };
    void fetchReportData(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [fetchReportData]);

  const headerData = useMemo(() => {
    const gatewayName = formatSchoolHeaderName(
      schoolDetails?.name,
      schoolDetails?.districtName
    );
    return {
      ...(reportData?.metadata ?? {}),
      school_name: gatewayName ?? reportData?.metadata?.school_name ?? trimmedSchoolId,
    };
  }, [reportData?.metadata, schoolDetails?.name, schoolDetails?.districtName, trimmedSchoolId]);

  const participation = reportData?.participation_details?.[0];
  const participationParts = splitParticipation(participation?.total_participation);

  return (
    <div className="school-report-page">
      <div className="Rep_main_cont">
        <SchoolHeader data={headerData} />

        <div className="SB_main_Cont">
          {loading ? (
            <div className="Loader">
              <img src={loaderGif} alt="loading" />
              <h4>Wait we are preparing your reports....</h4>
            </div>
          ) : error || !reportData ? (
            <div className="Loader">
              <p>{error}</p>
              <button
                type="button"
                className="RefetchBtn"
                onClick={() => {
                  setError(null);
                  void fetchReportData();
                }}
              >
                Retry
              </button>
            </div>
          ) : (
            <ReportProvider reportData={reportData}>
              <>
                <h3 className="Rep_sub-head">Summary</h3>
                <Summary data={reportData.summary} />

                <h3 className="Rep_sub-head">
                  Participation Details{' '}
                  <DynamicToolTip
                    content={`This section shows that ${
                      participationParts?.[0] ?? '0'
                    } students attempted the test, out of ${
                      participationParts?.[1] ?? '0'
                    } students registered for this event.`}
                    onClick={() => undefined}
                  >
                    <span>
                      {' '}
                      <img className="EMRe_info" src={i} alt="i" />
                    </span>
                  </DynamicToolTip>
                </h3>

                <div className="SB_Rep_card_cont">
                  <div
                    className="SB_Rep_card"
                    style={{ flexDirection: 'column', position: 'relative' }}
                  >
                    <img src={target} className="SR_targetimg" alt="target" />
                    <div>
                      <div className="SB_card_title">
                        <span>
                          <div className="SB_buedot1" />
                        </span>
                        Total Participation
                      </div>
                      <h4 className="SB_tp_Students">
                        {participationParts ? (
                          <>
                            {participationParts[0]}
                            <span>/{participationParts[1]}</span>
                          </>
                        ) : (
                          <span>0</span>
                        )}
                      </h4>
                    </div>
                    <DoubleProgressBar
                      participation={participation?.overall_participation_percent}
                      average={participation?.other_schools_avg_percent}
                    />
                  </div>
                  <div className="SB_Rep_card">
                    <GradeWiseParticipationChart
                      data={participation?.gradewise_participation ?? []}
                    />
                  </div>
                </div>

                <h3 className="Rep_sub-head">
                  Gradewise Details{' '}
                  <DynamicToolTip
                    content="This section provides a detailed performance breakdown for each grade across different subjects."
                    onClick={() => undefined}
                  >
                    <span>
                      {' '}
                      <img className="EMRe_info" src={i} alt="i" />
                    </span>
                  </DynamicToolTip>
                </h3>
                <GradeWiseDetails data={reportData.gradewise_data ?? []} />
              </>
            </ReportProvider>
          )}
        </div>

        <Footer />
      </div>
    </div>
  );
}
